import { Stack } from '@mui/material';
import { TValidationResults } from '@verifime/api-definition';
import {
  DisplayErrors,
  GenerateFormFields_New,
  generateNonFieldErrors,
  getFieldsRules_New,
  showFieldsErrors,
  TErrors,
  TFormFieldsAndRules_New,
  useCustomForm,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { medicareCardFieldsAndRules, medicareCardOwnersFieldsAndRules } from './formFieldConfigs';
import { MedicareCardOwnersInputs } from './MedicareCardOwnersInputs';
import {
  buildOcrData,
  checkIsEmptyForm,
  filterDocumentData,
  objectMedicareCardOwnersToArray,
  TOcrData,
  TSupportedOcrDocumentType,
} from './utils';

export const OcrMedicareCard = ({
  initialData,
  documentType,
  onFormChange,
  validationResults,
}: Readonly<{
  initialData: TOcrData;
  documentType: TSupportedOcrDocumentType;
  onFormChange?: (isValid: boolean, data: TOcrData) => void;
  validationResults: TValidationResults;
}>) => {
  /**
   * TODO: We need to further clean the code to reduce the duplication
   */
  const [fieldsAndRules, setFieldsAndRules] = useState<TFormFieldsAndRules_New>(
    medicareCardFieldsAndRules,
  );
  const medicareCardOwnersData = initialData?.medicareCardOwners;
  const medicareCardOwners = useMemo(
    () => objectMedicareCardOwnersToArray(medicareCardOwnersData),
    [medicareCardOwnersData],
  );
  const documentData = useMemo(
    () => filterDocumentData(initialData, documentType, fieldsAndRules),
    [initialData, documentType, fieldsAndRules],
  );
  const methods = useCustomForm<Record<string, any>>({
    defaultValues: documentData || {},
    schema: getFieldsRules_New({ ...fieldsAndRules, ...medicareCardOwnersFieldsAndRules }),
  });
  const {
    setValue,
    getValues,
    trigger,
    watch,
    formState: { isValid },
    setError,
  } = methods;

  const isMounted = useRef(true);

  const [nonFieldErrors, setNonFieldErrors] = useState<TErrors>([]);

  useEffect(() => {
    if (validationResults?.length < 1) {
      return;
    }
    showFieldsErrors(validationResults, setError);
    const nonFieldErrors = generateNonFieldErrors(validationResults);
    setNonFieldErrors(nonFieldErrors);
  }, [validationResults, setError]);

  useEffect(() => {
    if (medicareCardOwners?.length < 1) {
      setFieldsAndRules({ ...medicareCardFieldsAndRules, ...medicareCardOwnersFieldsAndRules });
    }
  }, [medicareCardOwners]);

  useEffect(() => {
    if (Object.keys(initialData || {}).length < 1) {
      return;
    }
    Object.entries(initialData).forEach(([k, v]) => setValue(k, v));
  }, [initialData, setValue]);

  useEffect(() => {
    if (!isMounted.current) {
      return;
    }
    const checkInitial = async () => {
      const values = getValues();
      const isEmptyForm = checkIsEmptyForm(values);
      let valid = false;
      if (!isEmptyForm) {
        valid = await trigger();
      }
      onFormChange?.(valid, buildOcrData(documentType, values));
    };
    checkInitial();
    return () => {
      isMounted.current = false;
    };
  }, [getValues, trigger, onFormChange, documentType]);

  // Watch for subsequent changes
  useEffect(() => {
    const subscription = watch(async (formData) => {
      // Only trigger validation and call onFormChange if form data actually changes
      const isEmptyForm = checkIsEmptyForm(formData);
      let valid = false;
      if (!isEmptyForm) {
        valid = await trigger();
      }
      onFormChange?.(valid, buildOcrData(documentType, formData));
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger, onFormChange, documentType]);

  const handleOnOwnerSelected = useCallback(async () => {
    await trigger();
    onFormChange?.(isValid, buildOcrData(documentType, getValues()));
  }, [documentType, getValues, isValid, onFormChange, trigger]);

  return (
    <FormProvider {...methods}>
      <Stack mt={tokens.spacingLg}>
        <GenerateFormFields_New
          formFieldsAndRules={fieldsAndRules}
          control={methods.control}
          errors={methods.formState.errors}
          formFieldsWithDefaultValues={documentData}
        />
        {medicareCardOwners && (
          <MedicareCardOwnersInputs
            medicareCardOwners={medicareCardOwners}
            onOwnerSelected={handleOnOwnerSelected}
          />
        )}
        <DisplayErrors errors={nonFieldErrors} />
      </Stack>
    </FormProvider>
  );
};
